import { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormDrawer } from '../ui/FormDrawer';
import { StatusBadge } from '../ui/StatusBadge';
import { useEquipmentForm } from '../../hooks/useEquipmentForm';
import type { RackEquipment } from '../../types';
import type { CatalogEntry, AISuggestion } from '../../utils/equipmentApi';
import { AlertCircle, AlertTriangle, Search, Zap } from 'lucide-react';
import { api } from '../../utils/api';
import {
  PDU_FEED_OPTIONS,
  PDU_PHASE_OPTIONS,
  PDU_AMPERAGE_OPTIONS,
  PDU_OUTLET_PRESETS,
  buildPduAttributes,
  parsePduAttributes,
  type PduConfigValues,
} from '../../utils/equipmentPduForm.logic';
import { shouldOfferAutoPowerPatch } from '../../utils/equipmentAutoPatch.logic';

interface EquipmentFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  onCancel: () => void;
  initialData?: RackEquipment | null;
  rackUSlots?: number | null;
  rackMaxWeight?: number | null;
  rackMaxPower?: number | null;
  occupiedPositions?: number[];
  onValidate: (data: any) => Promise<{ errors: string[], warnings: string[] }>;
  catalogSearch: (query: string) => Promise<CatalogEntry[]>;
  onCatalogSelect: (entry: CatalogEntry) => void;
  onAISuggest: (query: string, type: string | undefined, targetU: number, onProgress: (phase: string) => void, signal?: AbortSignal) => Promise<AISuggestion>;
  currentWarnings?: string[];
  currentErrors?: string[];
  onReloadData?: () => void;
  isSaving?: boolean;
  currentWeight?: number;
  currentPower?: number;
  hasPdu?: boolean;
}

const EQUIPMENT_TYPES = [
  { value: 'server', label: 'Server' },
  { value: 'switch', label: 'Switch' },
  { value: 'router', label: 'Router' },
  { value: 'firewall', label: 'Firewall' },
  { value: 'storage', label: 'Storage' },
  { value: 'pdu', label: 'PDU' },
  { value: 'ups', label: 'UPS' },
  { value: 'patch_panel', label: 'Patch Panel' },
] as const;

const MOUNTING_POSITIONS = ['Front', 'Rear', 'Left', 'Right', 'Full Depth'];

export function EquipmentFormDrawer(props: EquipmentFormDrawerProps) {
  const {
    isOpen, onClose, onCancel,
    rackUSlots = null,
    rackMaxWeight = null, rackMaxPower = null,
    isSaving = false,
    currentWeight = 0, currentPower = 0,
    hasPdu = true,
  } = props;

  const {
    form: { control, handleSubmit, formState: { errors }, setValue, watch },
    searchQuery, setSearchQuery,
    searchResults, showCatalog, isSearching,
    validationResult,
    aiReasoning, isAILoading, aiPhase,
    handleCatalogSelect, handleAISuggest, onSubmit,
    watchedHeight, watchedPosition, watchedWeight, watchedPower
  } = useEquipmentForm(props);

  const watchedType = watch('equipment_type');
  const watchedAttrs = watch('attributes');
  const watchedMounting = watch('mounting_position');
  const watchedIsZeroU = watch('is_zero_u');
  const isPdu = watchedType === 'pdu';
  const isZeroU = Boolean(watchedIsZeroU || watchedMounting === 'Left' || watchedMounting === 'Right');
  const isEdit = Boolean(props.initialData?.id);
  const canAutoPatch = shouldOfferAutoPowerPatch({
    equipment_type: watchedType,
    is_zero_u: isZeroU,
    hasPdu,
    isEdit,
  });
  const pduConfig = parsePduAttributes(watchedAttrs);

  const handlePduConfigChange = (partial: Partial<PduConfigValues>) => {
    const current = parsePduAttributes(watch('attributes'));
    const updated = { ...current, ...partial };
    const jsonStr = buildPduAttributes(updated);
    setValue('attributes', jsonStr, { shouldDirty: true });

    const currentName = watch('name');
    if (!currentName || currentName.startsWith('PDU-') || currentName === 'PDU') {
      const feedLetter = updated.feedName.replace('Feed ', '').trim();
      setValue('name', `PDU-${feedLetter}`);
    }
    setValue('power_w', 0);
  };

  const handleToggleZeroU = (checked: boolean) => {
    setValue('is_zero_u', checked, { shouldDirty: true });
    if (checked) {
      setValue('u_height', 0);
      setValue('u_position', 0);
      setValue('mounting_position', 'Left');
    } else {
      setValue('u_height', 1);
      setValue('u_position', 1);
      setValue('mounting_position', 'Rear');
    }
  };

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Serial number lookup state
  type LookupResult = {
    manufacturer?: string; model?: string; confidence: string;
    source?: string; specs_source?: string;
    u_height?: number; weight_kg?: number; power_w?: number;
    equipment_type?: string; cpu_sockets?: number; max_ram_gb?: number;
    warranty_status?: string;
    error?: string;
  };
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);

  const handleSerialLookup = async () => {
    const serial = (document.getElementById('eq-serial') as HTMLInputElement)?.value?.trim();
    if (!serial || serial.length < 4) return;
    setIsLookingUp(true);
    setLookupResult(null);
    try {
      const res = await api.get('/api/equipment/serial-lookup', { params: { serial } });
      const d = res.data;
      setLookupResult(d);
      // Auto-fill form fields where data was found
      if (d.model && d.manufacturer) setValue('name', `${d.manufacturer} ${d.model}`);
      else if (d.model) setValue('name', d.model);
      if (d.power_w)        setValue('power_w', d.power_w);
      if (d.weight_kg)      setValue('weight_kg', d.weight_kg);
      if (d.u_height)       setValue('u_height', d.u_height);
      if (d.equipment_type) setValue('equipment_type', d.equipment_type.toLowerCase());
    } catch (e: any) {
      const msg = e.response?.data?.detail || 'Ошибка поиска';
      setLookupResult({ confidence: 'low', error: msg });
    } finally {
      setIsLookingUp(false);
    }
  };

  return (
    <FormDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={props.initialData && (props.initialData as any).id ? 'Редактировать оборудование' : 'Добавить оборудование в стойку'}
    >
      <form onSubmit={handleSubmit(data => { if (!props.currentErrors?.length) return onSubmit(data); })} className="space-y-6">
        {!!props.currentErrors?.length && <div role="alert" className="border border-border rounded-sm p-3 text-sm text-text-primary">
          {props.currentErrors.map(message => <p key={message}>{message}</p>)}
          {props.onReloadData && <button type="button" onClick={props.onReloadData} className="mt-2 min-h-11 px-3 border border-border rounded-sm focus-visible:outline-2 focus-visible:outline-accent">Повторить загрузку</button>}
        </div>}
        <div className="flex gap-3 items-start">
          <div className="flex-1">
            <label htmlFor="eq-catalog-search" className="block text-sm font-medium mb-1">Поиск в каталоге</label>
            <input
              id="eq-catalog-search"
              ref={searchInputRef}
              type="text"
              placeholder="Начните вводить название (HP, Cisco, Huawei...)"
              className="w-full px-3 py-2 rounded-lg border bg-bg-input border-border text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={handleAISuggest}
            disabled={isSaving || isAILoading}
            className="px-4 py-2 mt-6 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/30 disabled:opacity-50 whitespace-nowrap"
          >
            {isAILoading ? 'AI ...' : 'AI'}
          </button>
        </div>

        {isAILoading && (
          <div className="p-3 text-sm text-purple-300 bg-purple-500/10 rounded border border-purple-500/20 flex items-center space-x-2">
            <svg className="animate-spin h-4 w-4 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>AI: {aiPhase}</span>
          </div>
        )}
        {aiReasoning && (
          <div className="p-3 text-sm text-green-300 bg-green-500/10 rounded border border-green-500/20">
            {aiReasoning}
          </div>
        )}

        {showCatalog && (searchResults.length > 0 || isSearching) && (
          <div className="rounded-lg border border-border bg-bg-panel max-h-60 overflow-auto">
            {isSearching ? (
              <div className="p-4 text-center text-text-muted">Поиск...</div>
            ) : (
              searchResults.map((entry) => (
                <button
                  key={entry.id} type="button" onClick={() => handleCatalogSelect(entry)}
                  className="w-full p-3 text-left hover:bg-bg-hover border-b border-border last:border-b-0 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary">{entry.manufacturer} {entry.model}</div>
                      <div className="text-xs text-text-muted">{entry.equipment_type} · {entry.u_height}U · {entry.weight_kg}кг · {entry.power_watts || 0}Вт</div>
                    </div>
                    {entry.verified && <StatusBadge status="normal" label="Verified" />}
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        <div>
          <label htmlFor="eq-name" className="block text-sm font-medium mb-1">Название *</label>
          <Controller control={control} name="name" rules={{ required: 'Название обязательно' }} render={({ field }) => (
            <input {...field} id="eq-name" className={`w-full px-3 py-2 rounded-lg border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent ${errors.name ? 'border-red-500' : 'border-border'}`} />
          )} />
          {errors.name && <span className="text-xs text-status-critical">{errors.name.message}</span>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="eq-type" className="block text-sm font-medium mb-1">Тип *</label>
            <Controller control={control} name="equipment_type" render={({ field }) => (
              <select
                {...field}
                id="eq-type"
                onChange={(e) => {
                  const newType = e.target.value;
                  field.onChange(e);
                  if (newType === 'pdu') {
                    handleToggleZeroU(true);
                    handlePduConfigChange({});
                  } else if (watchedType === 'pdu' && newType !== 'pdu') {
                    handleToggleZeroU(false);
                    setValue('power_w', 300);
                  }
                }}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {EQUIPMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            )} />
          </div>
          <div>
            <label htmlFor="eq-u-height" className="block text-sm font-medium mb-1">Высота (U) *</label>
            {isZeroU ? (
              <div className="px-3 py-2 rounded-lg border border-border bg-bg-panel text-text-secondary text-sm">
                0U <span className="text-xs text-text-muted">(Вертикальный Zero-U профиль)</span>
              </div>
            ) : (
              <Controller
                control={control}
                name="u_height"
                rules={{
                  required: !isZeroU ? 'Высота обязательна' : false,
                  min: !isZeroU ? { value: 1, message: 'Минимум 1U' } : undefined,
                  max: { value: 42, message: 'Максимум 42U' }
                }}
                render={({ field }) => (
                  <input {...field} id="eq-u-height" type="number" className={`w-full px-3 py-2 rounded-lg border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent ${errors.u_height ? 'border-red-500' : 'border-border'}`} />
                )}
              />
            )}
            {errors.u_height && <span className="text-xs text-status-critical">{errors.u_height.message}</span>}
          </div>
        </div>

        {isPdu && (
          <div className="p-4 rounded-lg bg-bg-panel border border-accent/40 space-y-4">
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                <Zap className="w-4 h-4 text-amber-400" />
                <span>Конфигурация PDU / БРП</span>
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none text-text-secondary hover:text-text-primary">
                <input
                  type="checkbox"
                  checked={isZeroU}
                  onChange={(e) => handleToggleZeroU(e.target.checked)}
                  className="rounded border-border text-accent focus:ring-accent"
                />
                <span className="font-medium">Zero-U (В кармане стойки)</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <label className="block text-xs font-medium mb-1 text-text-secondary">Ввод питания (Feed)</label>
                <select
                  value={pduConfig.feedName}
                  onChange={(e) => handlePduConfigChange({ feedName: e.target.value })}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-bg-input text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {PDU_FEED_OPTIONS.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1 text-text-secondary">Фаза</label>
                <select
                  value={pduConfig.phase}
                  onChange={(e) => handlePduConfigChange({ phase: e.target.value })}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-bg-input text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {PDU_PHASE_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1 text-text-secondary">Номинальный ток (автомат)</label>
                <select
                  value={pduConfig.ratedAmperage}
                  onChange={(e) => handlePduConfigChange({ ratedAmperage: Number(e.target.value) })}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-bg-input text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {PDU_AMPERAGE_OPTIONS.map((a) => (
                    <option key={a} value={a}>{a} А ({a * 230 >= 1000 ? `${(a * 230 / 1000).toFixed(1)} кВт` : `${a * 230} Вт`})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1 text-text-secondary">Пресет розеток (Outlets)</label>
                <select
                  value={pduConfig.presetId}
                  onChange={(e) => handlePduConfigChange({ presetId: e.target.value })}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-bg-input text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {PDU_OUTLET_PRESETS.map((pr) => (
                    <option key={pr.id} value={pr.id}>{pr.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="text-[11px] text-text-muted flex items-center gap-1.5 pt-1">
              <span>⚡ Макс. нагрузка: <strong className="text-text-primary">{pduConfig.ratedAmperage * 230} Вт</strong></span>
              <span>•</span>
              <span>Разъёмы: <strong className="text-text-primary">{(PDU_OUTLET_PRESETS.find(p => p.id === pduConfig.presetId) || PDU_OUTLET_PRESETS[0]).label}</strong></span>
              {isZeroU && <span>• <span>Не занимает юниты стойки</span></span>}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="eq-u-position" className="block text-sm font-medium mb-1">U-позиция *</label>
            {isZeroU ? (
              <div className="px-3 py-2 rounded-lg border border-border bg-bg-panel text-text-secondary text-sm">
                0 <span className="text-xs text-text-muted">(Боковой профиль PDU)</span>
              </div>
            ) : (
              <Controller
                control={control}
                name="u_position"
                rules={{
                  required: !isZeroU ? 'Позиция обязательна' : false,
                  min: !isZeroU ? { value: 1, message: 'Минимум 1' } : undefined,
                  max: rackUSlots == null ? undefined : { value: rackUSlots, message: `Максимум ${rackUSlots}` }
                }}
                render={({ field }) => (
                  <input {...field} id="eq-u-position" type="number" className={`w-full px-3 py-2 rounded-lg border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent ${errors.u_position ? 'border-red-500' : 'border-border'}`} />
                )}
              />
            )}
            {errors.u_position && <span className="text-xs text-status-critical">{errors.u_position.message}</span>}
          </div>
          <div>
            <label htmlFor="eq-mounting" className="block text-sm font-medium mb-1">Монтаж</label>
            <Controller control={control} name="mounting_position" render={({ field }) => (
              <select {...field} id="eq-mounting" className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent">
                {isZeroU
                  ? ['Left', 'Right'].map(p => <option key={p} value={p}>{p === 'Left' ? 'Левый карман (Left)' : 'Правый карман (Right)'}</option>)
                  : MOUNTING_POSITIONS.map(p => <option key={p} value={p}>{p}</option>)
                }
              </select>
            )} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="eq-weight" className="block text-sm font-medium mb-1">Вес (кг)</label>
            <Controller control={control} name="weight_kg" render={({ field }) => (
              <input {...field} value={field.value ?? ''} onChange={e => field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)} placeholder="Неизвестно" id="eq-weight" type="number" step="0.1" min="0" className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent" />
            )} />
            <div className={`mt-1 text-xs ${rackMaxWeight != null && currentWeight + (watchedWeight || 0) > rackMaxWeight ? 'text-status-critical font-bold' : 'text-text-muted'}`}>Известная масса стойки: {currentWeight} кг · прибор: {watchedWeight == null ? 'неизвестно' : `${watchedWeight} кг`} · {rackMaxWeight == null ? 'лимит неизвестен' : `лимит ${rackMaxWeight} кг`}</div>
          </div>
          <div>
            <label htmlFor="eq-power" className="block text-sm font-medium mb-1">Мощность (Вт)</label>
            {isPdu ? (
              <div className="px-3 py-2 rounded-lg border border-border bg-bg-panel text-text-secondary text-sm flex items-center justify-between">
                <span>0 Вт</span>
                <span className="text-xs text-text-muted">PDU распределяет питание</span>
              </div>
            ) : (
              <Controller control={control} name="power_w" render={({ field }) => (
                <input {...field} value={field.value ?? ''} onChange={e => field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)} placeholder="Неизвестно" id="eq-power" type="number" min="0" step="10" className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent" />
              )} />
            )}
            <div className={`mt-1 text-xs ${rackMaxPower != null && currentPower + (watchedPower || 0) > rackMaxPower ? 'text-status-critical font-bold' : 'text-text-muted'}`}>Известная мощность стойки: {currentPower} Вт · прибор: {watchedPower == null ? 'неизвестно' : `${watchedPower} Вт`} · {rackMaxPower == null ? 'лимит неизвестен' : `лимит ${rackMaxPower} Вт`}</div>
          </div>
        </div>

        {canAutoPatch && (
          <div className="p-3 rounded-lg border border-border bg-bg-panel flex items-start gap-3">
            <Controller
              control={control}
              name="auto_patch_power"
              render={({ field }) => (
                <input
                  id="eq-auto-patch-power"
                  type="checkbox"
                  name={field.name}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  checked={Boolean(field.value)}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-border text-accent focus:ring-accent cursor-pointer"
                />
              )}
            />
            <div className="flex-1 min-w-0">
              <label htmlFor="eq-auto-patch-power" className="text-sm font-medium text-text-primary cursor-pointer flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-accent shrink-0" />
                <span>Подключить блоки питания к PDU A/B</span>
              </label>
              <p className="text-xs text-text-muted mt-0.5">
                Создаёт подключения PSU 1 и PSU 2 к свободным розеткам PDU-A и PDU-B.
                Независимость источников питания этим не подтверждается.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
          <label htmlFor="eq-serial" className="block text-sm font-medium mb-1">Серийный номер (SN)</label>
            <div className="flex gap-2">
              <Controller control={control} name="serial_number" render={({ field }) => (
                <input {...field} id="eq-serial" type="text" placeholder="SN-994182" className="flex-1 px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent" />
              )} />
              <button
                type="button"
                onClick={handleSerialLookup}
                disabled={isLookingUp}
                title="Автозаполнение по серийному номеру"
                className="px-3 py-2 rounded-lg border border-border bg-bg-panel text-text-secondary hover:bg-bg-hover hover:text-text-primary disabled:opacity-50 transition flex items-center gap-1 text-sm"
              >
                {isLookingUp ? (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </button>
            </div>
            {lookupResult && (
              <div className={`mt-2 rounded-lg border text-xs overflow-hidden ${
                lookupResult.error ? 'border-red-800/50 bg-red-950/20' :
                lookupResult.confidence === 'high' ? 'border-green-800/40 bg-green-950/20' :
                lookupResult.confidence === 'medium' ? 'border-amber-800/40 bg-amber-950/20' :
                'border-[var(--border-color)] bg-[var(--bg-input)]'
              }`}>
                {lookupResult.error ? (
                  <p className="px-3 py-2 text-red-400">{lookupResult.error}</p>
                ) : (
                  <>
                    {/* Header row */}
                    <div className="px-3 py-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {lookupResult.manufacturer && (
                          <span className="font-bold text-text-primary shrink-0">{lookupResult.manufacturer}</span>
                        )}
                        {lookupResult.model && (
                          <span className="text-text-secondary truncate">{lookupResult.model}</span>
                        )}
                        {!lookupResult.manufacturer && !lookupResult.model && (
                          <span className="text-text-muted">Производитель не определён</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {lookupResult.source && (
                          <span className="text-[10px] text-text-muted bg-bg-panel px-1.5 py-0.5 rounded">{lookupResult.source}</span>
                        )}
                      </div>
                    </div>

                    {/* Specs badges */}
                    {(lookupResult.u_height || lookupResult.weight_kg || lookupResult.power_w || lookupResult.cpu_sockets || lookupResult.max_ram_gb || lookupResult.warranty_status) && (
                      <div className="px-3 pb-2 flex flex-wrap gap-1.5 border-t border-border/30 pt-2">
                        {lookupResult.u_height && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-bg-panel border border-border text-text-secondary">
                            <span className="text-[10px] text-text-muted">Высота</span>
                            <span className="font-mono font-bold text-text-primary">{lookupResult.u_height}U</span>
                          </span>
                        )}
                        {lookupResult.weight_kg && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-bg-panel border border-border text-text-secondary">
                            <span className="text-[10px] text-text-muted">Вес</span>
                            <span className="font-mono font-bold text-text-primary">{lookupResult.weight_kg} кг</span>
                          </span>
                        )}
                        {lookupResult.power_w && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-bg-panel border border-amber-800/40 text-amber-300/80">
                            <span className="text-[10px]">⚡</span>
                            <span className="font-mono font-bold">{lookupResult.power_w} Вт</span>
                          </span>
                        )}
                        {lookupResult.cpu_sockets && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-bg-panel border border-border text-text-secondary">
                            <span className="text-[10px] text-text-muted">CPU</span>
                            <span className="font-mono font-bold text-text-primary">{lookupResult.cpu_sockets}×</span>
                          </span>
                        )}
                        {lookupResult.max_ram_gb && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-bg-panel border border-border text-text-secondary">
                            <span className="text-[10px] text-text-muted">RAM max</span>
                            <span className="font-mono font-bold text-text-primary">
                              {lookupResult.max_ram_gb >= 1024 ? `${lookupResult.max_ram_gb / 1024} ТБ` : `${lookupResult.max_ram_gb} ГБ`}
                            </span>
                          </span>
                        )}
                        {lookupResult.warranty_status && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium ${
                            lookupResult.warranty_status.toLowerCase().includes('active') || lookupResult.warranty_status.toLowerCase().includes('active')
                              ? 'border-green-800/40 text-green-400 bg-green-950/20'
                              : 'border-red-800/40 text-red-400 bg-red-950/20'
                          }`}>
                            🛡 {lookupResult.warranty_status}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
          <div>
            <label htmlFor="eq-asset-tag" className="block text-sm font-medium mb-1">Инвентарный номер (Asset Tag)</label>
            <Controller control={control} name="asset_tag" render={({ field }) => (
              <input {...field} id="eq-asset-tag" type="text" placeholder="TAG-1049" className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent" />
            )} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="eq-status" className="block text-sm font-medium mb-1">Статус устройства</label>
            <Controller control={control} name="status" render={({ field }) => (
              <select {...field} id="eq-status" className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent">
                <option value="">Неизвестно</option>
                <option value="Active">Active (Активен)</option>
                <option value="Staged">Staged (В резерве)</option>
                <option value="Failed">Failed (Сбой)</option>
                <option value="Decommissioned">Decommissioned (Списан)</option>
                <option value="Maintenance">Maintenance (Техобслуживание)</option>
              </select>
            )} />
          </div>
          <div>
            <label htmlFor="eq-airflow" className="block text-sm font-medium mb-1">Направление обдува</label>
            <Controller control={control} name="airflow" render={({ field }) => (
              <select {...field} id="eq-airflow" className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent">
                <option value="">Неизвестно</option>
                <option value="Front-to-Rear">Front-to-Rear (Спереди назад)</option>
                <option value="Rear-to-Front">Rear-to-Front (Сзади вперед)</option>
                <option value="Passive">Passive (Пассивное)</option>
              </select>
            )} />
          </div>
        </div>

        <div>
          <label htmlFor="eq-tenant" className="block text-sm font-medium mb-1">
            Владелец / Арендатор (Tenant)
          </label>
          <Controller
            control={control}
            name="tenant"
            render={({ field }) => (
              <input
                {...field}
                id="eq-tenant"
                type="text"
                placeholder="По умолчанию (наследуется от стойки)"
                className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent text-sm"
              />
            )}
          />
          <span className="text-[11px] text-text-muted mt-0.5 block">
            Оставьте пустым для автоматического наследования владельца стойки
          </span>
        </div>

        <div>
          <label htmlFor="eq-attributes" className="block text-sm font-medium mb-1">Атрибуты (JSON)</label>
          <Controller control={control} name="attributes" render={({ field }) => (
            <textarea {...field} id="eq-attributes" rows={3} className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-text-primary font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
          )} />
        </div>

        {(validationResult.errors.length > 0 || validationResult.warnings.length > 0) && (
          <div className="space-y-2">
            {validationResult.errors.map((e, i) => (
              <div key={`e-${i}`} className="flex items-center gap-2 text-sm text-status-critical"><AlertCircle size={14} className="shrink-0" /> <span>{e}</span></div>
            ))}
            {validationResult.warnings.map((w, i) => (
              <div key={`w-${i}`} className="flex items-center gap-2 text-sm text-status-warning"><AlertTriangle size={14} className="shrink-0" /> <span>{w}</span></div>
            ))}
          </div>
        )}

        <div className="rounded-lg bg-bg-panel border border-border p-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-text-muted">Занимает:</span> <span className="font-mono">{isZeroU ? '0U (Zero-U)' : `${watchedHeight}U`}</span></div>
            <div><span className="text-text-muted">Позиция:</span> <span className="font-mono">{isZeroU ? (watchedMounting === 'Right' ? 'Правый карман (0U)' : 'Левый карман (0U)') : `U${watchedPosition}`}</span></div>
            <div><span className="text-text-muted">Вместимость стойки:</span> <span className="font-mono">{rackUSlots == null ? 'Неизвестно' : `${rackUSlots}U`}</span></div>
            <div><span className="text-text-muted">Вес:</span> <span className="font-mono">{watchedWeight == null ? 'Неизвестно' : `${watchedWeight} кг`}</span></div>
            <div className="flex justify-between items-center text-sm py-1 border-t border-border col-span-2">
              <span className="text-text-muted">Мощность прибора:</span>
              <span className="font-mono">{isPdu ? 'PDU распределяет питание' : watchedPower == null ? 'Неизвестно' : `${watchedPower} Вт`}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-border text-text-secondary hover:bg-bg-hover">Отмена</button>
          <button type="submit" disabled={isSaving || !!props.currentErrors?.length} className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 disabled:opacity-50">{isSaving ? 'Сохранение...' : 'Сохранить'}</button>
        </div>
      </form>
    </FormDrawer>
  );
}
